import { useState } from "react";

export type FaqContent = { question: string; answer: string };

export const PRICING_FAQS: FaqContent[] = [
  {
    question: "Do unused Treats roll over?",
    answer:
      "Monthly plan Treats reset at the start of each cycle — they don't roll over. Treats you buy in a Treat Pack never expire.",
  },
  {
    question: "What happens when I run out?",
    answer:
      "You'll see a friendly message letting you know you're out of Treats. No surprise charges — it's a hard stop.",
  },
  {
    question: "Will I get a warning before I run out?",
    answer:
      "Yes. When your Treat balance gets low, it'll turn yellow, so you can decide whether to top up or wait for your next refill.",
  },
  {
    question: "Do different Companions cost different amounts?",
    answer:
      "Yes. Not all tasks are created equal. A Summi-rization that processes lengthy documents typically costs more Treats than a quick Quill edit or a Sage Q&A. This reflects the actual work being done, not arbitrary pricing.",
  },
  {
    question: "Can I buy a Treat Pack if I'm already on a monthly plan?",
    answer:
      "Yes. Treats in a Pack stack on top of your monthly plan. They are available to everyone.",
  },
  {
    question: "What if Companion gives me a bad response?",
    answer:
      "Treats are only deducted when a response is successfully delivered. If you feel a response wasn't worth the Treats it cost, reach out to our Delight team — they have the tools and authority to credit Treats back to your account.",
  },
  {
    question: "Is there a team or clinic-level plan?",
    answer:
      "Not at launch. Treats are per-user for now. If a clinic owner wants to fund a team member's usage, they'd purchase through that user's account. Team-level billing is on the roadmap.",
  },
  {
    question: "What about my existing Scribenote plan?",
    answer:
      "Nothing changes. Your Scribenote plan stays exactly as it is. Companion is a separate product you can add on whenever you're ready.",
  },
];

export function PricingFAQ() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="px-[clamp(24px,5vw,80px)] py-[clamp(72px,9vw,112px)]">
      <div className="mx-auto max-w-[880px]">
        <div className="mb-11 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-indigo-700">
            Frequently asked
          </div>
          <h2 className="font-companion mx-auto max-w-[640px] text-[clamp(30px,3.8vw,44px)] font-bold tracking-[-0.025em] text-primary [text-wrap:balance]">
            Questions about Treats and plans.
          </h2>
        </div>

        <div className="flex flex-col gap-2.5">
          {PRICING_FAQS.map((f, i) => {
            const isOpen = open === i;
            const panelId = `pricing-faq-panel-${i}`;
            const buttonId = `pricing-faq-button-${i}`;
            return (
              <div
                key={i}
                className={`rounded-2xl border bg-white font-body transition-[box-shadow,border-color] duration-200 ${
                  isOpen
                    ? "border-indigo-300 shadow-[0_14px_32px_-16px_rgba(99,102,241,0.32)]"
                    : "border-grindigo-100 shadow-[0_2px_8px_-4px_rgba(99,102,241,0.08)]"
                }`}
              >
                <button
                  type="button"
                  id={buttonId}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full cursor-pointer items-center justify-between gap-4 rounded-2xl border-0 bg-transparent px-6 py-5 text-left font-body"
                >
                  <span className="text-base font-semibold text-primary">
                    {f.question}
                  </span>
                  <span
                    aria-hidden="true"
                    className={`grid h-7 w-7 shrink-0 place-items-center rounded-full transition-[transform,background,color] duration-200 ${
                      isOpen
                        ? "rotate-45 bg-indigo-600 text-white"
                        : "bg-indigo-50 text-indigo-700"
                    }`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 5v14M5 12h14"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
                {isOpen && (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className="px-6 pb-5 text-[15px] leading-[1.65] text-secondary [text-wrap:pretty]"
                    dangerouslySetInnerHTML={{ __html: f.answer }}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8 text-center text-sm text-secondary">
          Still curious? Email{" "}
          <a
            href="mailto:support@scribenote.com"
            className="font-semibold text-indigo-600"
          >
            support@scribenote.com
          </a>{" "}
          and our Customer Delight team will get back to you.
        </div>
      </div>
    </section>
  );
}
